import React, { useEffect } from 'react';
import { useImmer } from 'use-immer';
import { GuestRegistryRouter } from './GuestRegistry.routes';
import { Route, Switch } from '@react-router';
import { useGuestRegistryController, GuestRegistryState } from './GuestRegistry.controller';
import { GuestRegistryProvider as ReactProvider } from './state';
import { Purchase } from './routes/Purchase';
import { RegistryFontFaces } from '@apps/registry/common/util/fonts';
import { TelemetryProvider, useGuestRegistryTelemetry } from './GuestRegistry.telemetry';
import { RegistryProtected } from './routes/GuestRegistry/components/RegistryProtected';
import { ErrorSplash } from '@shared/components';
import { RegistryHidden } from './routes/GuestRegistry/components';
import { usePurchaseContextIdFromLocation } from '../common/utils/dropship';
import { JoyKitThemeProvider } from '@withjoy/joykit';
import { StoredGiftReservationFieldsProvider } from './routes/GuestRegistry/components/StoredGiftReservationFieldsProvider';
import { PurchaseConfirmationModalProvider } from '@apps/registry/guest/routes/Purchase/components/PurchaseConfirmationModal/PurchaseConfirmationModal.provider';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { NoSsr } from '@shared/components/NoSsr';
import { EventDesignPurpose, EventPageType, GuestEventDetailsByNameQuery, useGetEventRegistriesAndOrdersQuery, useGuestEventDetailsByNameQuery } from '@graphql/generated';
import { GuestRegistryHead } from './GuestRegistryHead';
import { EventDesignThemingProvider } from '@shared/core/website/theming';
import { DropshipPurchaseConfirmationV2 } from '../common/components/DropshipPurchaseConfirmation';
import { ShoppingCart } from '../common/components/ShoppingCart';
import { ExpressCheckout } from '../common/components/ExpressCheckout/ExpressCheckout';
import { isInIframe } from '@shared/utils/isInIframe';
import { OnPreviewListenerUpdate } from './GuestRegistryPreviewListener';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { shouldUseEventDesignDraft } from './GuestRegistry.utils';
import { ColorPaletteProvider } from '@apps/guest';
import { useFeatureValue } from '@shared/core/featureFlags';
import AccommodationsOnRegistryConfirmation from '@apps/guest/routes/AccommodationsOnRegistryConfirmation';
import { useQueryParamHelper } from '@shared/core/queryString';
import { CartCheckout } from './routes/CartCheckout';
import { GuestShoppingProvider } from './routes/GuestRegistry/components/GuestShoppingProvider';
import GuestRegistryList from './routes/GuestRegistry';
import { RegistryShell } from './components/RegistryShell';

interface GuestRegistryProviderProps {
  eventHandle: string;
}
interface GuestRegistryProps extends GuestRegistryProviderProps {
  handleOnPreviewUpdate: OnPreviewListenerUpdate;
  ssrEventData?: GuestEventDetailsByNameQuery['eventByName'];
}

type PurchaseRouteSwitchProps = Readonly<{
  eventHandle: string;
  registry: GuestRegistryState;
  isRegistryHidden: boolean;
}>;

export const PurchaseRouteSwitch = (props: PurchaseRouteSwitchProps) => {
  const purchaseContextId = usePurchaseContextIdFromLocation();

  if (purchaseContextId) {
    // `pcId` is present after user goes through stripe payment flow and is redirected
    return <DropshipPurchaseConfirmationV2 registries={props.registry.registry} />;
  } else {
    // Default to the existing purchase confirmation page.
    return (
      <Purchase
        isAdmin={props.registry.isAdmin}
        eventId={props.registry.eventId}
        eventHandle={props.eventHandle}
        registry={props.registry.registry}
        shippingAddress={props.registry.shippingAddress}
        isRegistryHidden={props.isRegistryHidden}
      />
    );
  }
};

const GuestRegistry: React.FC<GuestRegistryProps> = props => {
  const { registry, loading, routes, location, canAccessPage, eventId, error } = useGuestRegistryController(props.eventHandle);
  const { ssrEventData } = props;
  const { trackError } = useGuestRegistryTelemetry();
  const isMobileOrTablet = useIsMobileOrTablet();
  const paramHelper = useQueryParamHelper();
  const expressCheckoutFeatureFlag = useFeatureValue('registryExpressCheckoutEnabled');

  // Prefetch registry items to warm Apollo cache during Phase 1.
  // Capturing `loading` to gate Phase 1, ensuring the cache is warm
  // before GuestShopping mounts (eliminates Phase 2 skeleton).
  const { loading: registryItemsLoading } = useGetEventRegistriesAndOrdersQuery({
    batchMode: 'fast',
    variables: { eventHandle: props.eventHandle },
  });

  // Extended Phase 1: keep skeleton visible until registry items are cached.
  // For admins with warm cache, registryItemsLoading is false immediately — no impact.
  // For guests on cold load, this extends Phase 1 by the registry items query duration.
  // Note: Removed (!!registry && !host) check - translations loading shouldn't gate the entire
  // loading state. If translations aren't ready when content mounts, the display name will
  // briefly show a fallback value, which is better than keeping the skeleton visible.
  const isPhase1 = loading || registryItemsLoading;

  const hasPhoto = !!(ssrEventData?.pageInfo?.page?.photo?.url || ssrEventData?.photo?.url);

  let content: JSX.Element | null = null;

  if (isPhase1) {
    // Show themed skeleton during Phase 1 (SSR data should always be available)
    if (ssrEventData) {
      content = (
        <RegistryShell
          eventHandle={props.eventHandle}
          eventDesign={ssrEventData.eventDesign}
          displayName={ssrEventData.info?.eventDisplayName ?? undefined}
          pages={ssrEventData.pages}
          hasPhoto={hasPhoto}
          layout={ssrEventData.eventDesign?.websiteLayout?.layoutType}
        />
      );
    }
  } else if (!registry) {
    content = null;
  } else if (error) {
    trackError('guestRegistry', error);
    content = <ErrorSplash />;
  } else if (canAccessPage) {
    const sessionEmail = registry?.registry?.filter(registry => registry.id === eventId)[0]?.mySession?.email;
    content = (
      <GuestShoppingProvider
        eventHandle={props.eventHandle}
        eventId={eventId}
        coupleAddress={registry.shippingAddress}
      >
        <PurchaseConfirmationModalProvider>
          <ColorPaletteProvider eventDesign={registry.eventDesign}>
            <TelemetryProvider context={{ eventId: registry.eventId, section: '' }}>
              <Switch location={location}>
                <Route
                  exact={true}
                  path={routes.guestRegistry.path}
                  render={() => (
                    <GuestRegistryList
                      eventHandle={props.eventHandle}
                      eventId={eventId!}
                      registryState={registry}
                      handleOnPreviewUpdate={props.handleOnPreviewUpdate}
                    />
                  )}
                />
                <Route
                  exact={true}
                  path={routes.purchase.path}
                  render={() => {
                    return (
                      <PurchaseRouteSwitch
                        eventHandle={props.eventHandle}
                        registry={registry}
                        isRegistryHidden={!!registry.isRegistryPageDisabled}
                      />
                    );
                  }}
                />
                <Route
                  exact={true}
                  path={routes.accommodations.path}
                  render={() => (
                    <AccommodationsOnRegistryConfirmation
                      eventHandle={props.eventHandle}
                      eventId={eventId!}
                    />
                  )}
                />
                {expressCheckoutFeatureFlag.value === 'on' && (
                  <Route
                    exact={true}
                    path={[routes.cartCheckout.path, routes.itemCheckout.path]}
                    render={routeProps => {
                      const registryItemId = routeProps.match.params.registryItemId;
                      if (registryItemId) {
                        const quantity = paramHelper.getValueInteger('q');
                        return (
                          <ExpressCheckout
                            eventHandle={props.eventHandle}
                            eventId={eventId!}
                            registryState={registry}
                            purchaseItems={[{ registryItemId, quantity }]}
                          />
                        );
                      }
                      return (
                        <CartCheckout
                          eventHandle={props.eventHandle}
                          eventId={eventId!}
                          registryState={registry}
                        />
                      );
                    }}
                  />
                )}
                {/* TODO: extract out contribute route */}
                <Route
                  exact={true}
                  path={routes.contribute.path}
                  render={() => (
                    <StoredGiftReservationFieldsProvider sessionEmail={sessionEmail}>
                      <Purchase
                        eventHandle={props.eventHandle}
                        registry={registry?.registry}
                        eventId={registry?.eventId}
                        isAdmin={registry.isAdmin}
                        shippingAddress={registry.shippingAddress}
                        isRegistryHidden={!!registry.isRegistryPageDisabled}
                        layoutOption={'donationFund'}
                      />
                    </StoredGiftReservationFieldsProvider>
                  )}
                />
                {/* For native apps */}
                <Route
                  exact={true}
                  path={routes.shoppingCart.path}
                  render={() => (
                    <>
                      {!isMobileOrTablet ? (
                        <GuestRegistryList
                          eventHandle={props.eventHandle}
                          eventId={eventId!}
                          registryState={registry}
                          handleOnPreviewUpdate={props.handleOnPreviewUpdate}
                        />
                      ) : (
                        <StoredGiftReservationFieldsProvider sessionEmail={sessionEmail}>
                          <ShoppingCart
                            eventId={eventId!}
                            isShoppingPage
                            registry={registry.registry}
                            orders={registry.orders}
                            shippingAddress={registry.shippingAddress}
                            registryCurrencyCode={registry.registryCurrencyCode}
                          />
                        </StoredGiftReservationFieldsProvider>
                      )}
                    </>
                  )}
                />
              </Switch>
            </TelemetryProvider>
          </ColorPaletteProvider>
        </PurchaseConfirmationModalProvider>
      </GuestShoppingProvider>
    );
  } else if (registry.isRegistryPageDisabled) {
    content = <RegistryHidden website={props.eventHandle} />;
  } else if (registry.isRegistryPagePasswordProtected) {
    content = (
      <RegistryProtected
        eventId={eventId}
        eventHandle={props.eventHandle}
      />
    );
  }

  return <JoyKitThemeProvider>{content}</JoyKitThemeProvider>;
};

const isPreviewing = isInIframe();

export const GuestRegistryProvider: React.FC<GuestRegistryProviderProps> = props => {
  const { data } = useGuestEventDetailsByNameQuery({
    variables: {
      eventHandle: props.eventHandle,
      pageType: EventPageType.registry,
      eventDesignPurpose: shouldUseEventDesignDraft() ? EventDesignPurpose.draft : EventDesignPurpose.live,
    },
    batchMode: 'fast',
    ssr: true,
    fetchPolicy: 'cache-first',
  });

  const [{ eventPreview }, setState] = useImmer<{ eventPreview: GuestEventDetailsByNameQuery | undefined }>(() => ({
    eventPreview: data,
  }));

  // If previewing, use the clone. Else, use the original.
  const event = isPreviewing ? eventPreview?.eventByName : data?.eventByName;

  // Preview

  // Initialize preview data when query resolves
  useEffect(() => {
    if (isPreviewing) {
      setState(draft => {
        draft.eventPreview = data;
      });
    }
  }, [setState, data]);

  // Respond to event preview updates if previewing
  const handleOnPreviewUpdate = useEventCallback<OnPreviewListenerUpdate>(cb => {
    if (isPreviewing) {
      setState(draft => {
        const eventDraft = draft.eventPreview?.eventByName;
        if (eventDraft) {
          cb(eventDraft);
        }
      });
    }
  });

  return (
    <>
      {event && (
        <GuestRegistryHead
          eventDisplayName={event.info?.eventDisplayName}
          imageUrl={event.pageInfo?.page.photo?.url || event.photo?.url}
          handle={event.website}
          ownerFirstName={event.info.ownerFirstName}
          ownerLastName={event.info.ownerLastName}
          fianceeFirstName={event.info.fianceeFirstName}
          pageData={event.pages}
          eventType={event.info.eventType}
        />
      )}

      <NoSsr>
        <RegistryFontFaces />
        <GuestRegistryRouter eventHandle={props.eventHandle}>
          <ReactProvider>
            <EventDesignThemingProvider eventDesign={event?.eventDesign}>
              <GuestRegistry
                {...props}
                handleOnPreviewUpdate={handleOnPreviewUpdate}
                ssrEventData={event}
              />
            </EventDesignThemingProvider>
          </ReactProvider>
        </GuestRegistryRouter>
      </NoSsr>
    </>
  );
};
